'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SurfaceAreaCalculator() {
  const t = useTranslations("tools.surfaceAreaCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // State for each shape
  const [sphereInputs, setSphereInputs] = useState({ radius: '', unit: 'm' });
  const [coneInputs, setConeInputs] = useState({ radius: '', height: '', unit: 'm' });
  const [cubeInputs, setCubeInputs] = useState({ edge: '', unit: 'm' });
  const [cylinderInputs, setCylinderInputs] = useState({ radius: '', height: '', unit: 'm' });
  const [rectangularInputs, setRectangularInputs] = useState({ length: '', width: '', height: '', unit: 'm' });
  const [capsuleInputs, setCapsuleInputs] = useState({ radius: '', height: '', unit: 'm' });

  // Results state
  const [sphereResult, setSphereResult] = useState<number | null>(null);
  const [coneResult, setConeResult] = useState<number | null>(null);
  const [cubeResult, setCubeResult] = useState<number | null>(null);
  const [cylinderResult, setCylinderResult] = useState<number | null>(null);
  const [rectangularResult, setRectangularResult] = useState<number | null>(null);
  const [capsuleResult, setCapsuleResult] = useState<number | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const unitConversions: { [key: string]: number } = {
    'm': 1,
    'cm': 0.01,
    'mm': 0.001,
    'ft': 0.3048,
    'in': 0.0254
  };

  const unitLabels: { [key: string]: string } = {
    'm': 'm²',
    'cm': 'cm²',
    'mm': 'mm²',
    'ft': 'ft²',
    'in': 'in²'
  };

  const convertToMeters = (value: number, unit: string): number => {
    return value * unitConversions[unit];
  };

  const convertFromMeters = (value: number, unit: string): number => {
    return value / unitConversions[unit];
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return num.toExponential(4);
    }
    return num.toFixed(4);
  };

  const calculateSphere = (e: React.FormEvent) => {
    e.preventDefault();
    const radius = parseFloat(sphereInputs.radius);
    if (isNaN(radius) || radius <= 0) return;

    const r = convertToMeters(radius, sphereInputs.unit);
    const area = 4 * Math.PI * r * r;
    setSphereResult(convertFromMeters(area, sphereInputs.unit));
  };

  const calculateCone = (e: React.FormEvent) => {
    e.preventDefault();
    const radius = parseFloat(coneInputs.radius);
    const height = parseFloat(coneInputs.height);
    if (isNaN(radius) || radius <= 0 || isNaN(height) || height <= 0) return;

    const r = convertToMeters(radius, coneInputs.unit);
    const h = convertToMeters(height, coneInputs.unit);
    const slantHeight = Math.sqrt(h * h + r * r);
    const area = Math.PI * r * (r + slantHeight);
    setConeResult(convertFromMeters(area, coneInputs.unit));
  };

  const calculateCube = (e: React.FormEvent) => {
    e.preventDefault();
    const edge = parseFloat(cubeInputs.edge);
    if (isNaN(edge) || edge <= 0) return;

    const a = convertToMeters(edge, cubeInputs.unit);
    const area = 6 * a * a;
    setCubeResult(convertFromMeters(area, cubeInputs.unit));
  };

  const calculateCylinder = (e: React.FormEvent) => {
    e.preventDefault();
    const radius = parseFloat(cylinderInputs.radius);
    const height = parseFloat(cylinderInputs.height);
    if (isNaN(radius) || radius <= 0 || isNaN(height) || height <= 0) return;

    const r = convertToMeters(radius, cylinderInputs.unit);
    const h = convertToMeters(height, cylinderInputs.unit);
    const area = 2 * Math.PI * r * (r + h);
    setCylinderResult(convertFromMeters(area, cylinderInputs.unit));
  };

  const calculateRectangular = (e: React.FormEvent) => {
    e.preventDefault();
    const length = parseFloat(rectangularInputs.length);
    const width = parseFloat(rectangularInputs.width);
    const height = parseFloat(rectangularInputs.height);
    if (isNaN(length) || length <= 0 || isNaN(width) || width <= 0 || isNaN(height) || height <= 0) return;

    const l = convertToMeters(length, rectangularInputs.unit);
    const w = convertToMeters(width, rectangularInputs.unit);
    const h = convertToMeters(height, rectangularInputs.unit);
    const area = 2 * (l * w + l * h + w * h);
    setRectangularResult(convertFromMeters(area, rectangularInputs.unit));
  };

  const calculateCapsule = (e: React.FormEvent) => {
    e.preventDefault();
    const radius = parseFloat(capsuleInputs.radius);
    const height = parseFloat(capsuleInputs.height);
    if (isNaN(radius) || radius <= 0 || isNaN(height) || height <= 0) return;

    const r = convertToMeters(radius, capsuleInputs.unit);
    const h = convertToMeters(height, capsuleInputs.unit);
    const area = 2 * Math.PI * r * (2 * r + h);
    setCapsuleResult(convertFromMeters(area, capsuleInputs.unit));
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      {}
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    shapesGrid: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1.5rem',
        marginBottom: '2rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(3, 1fr)' }
    ),
    shapeCard: {
      background: 'white',
      borderRadius: '1rem',
      padding: '1.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
      transition: 'all 0.3s ease',
    },
    shapeHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.25rem',
      paddingBottom: '1rem',
      borderBottom: '2px solid #e2e8f0',
    },
    shapeIcon: {
      fontSize: '2.5rem',
      filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.1))',
    },
    shapeTitle: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      {}
    ),
    formGroup: {
      marginBottom: '1rem',
    },
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500 },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      {}
    ),
    inputWrapper: {
      display: 'flex',
      gap: '0.5rem',
    },
    input: getResponsiveStyle(
      {
        flex: 1,
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1.125rem' },
      { padding: '1rem', fontSize: '1.125rem' }
    ),
    select: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        background: 'white',
        cursor: 'pointer',
        minWidth: '100px',
      },
      { padding: '0.75rem', fontSize: '0.875rem' },
      { padding: '0.875rem', fontSize: '0.95rem', minWidth: '120px' },
      {}
    ),
    buttonGroup: getResponsiveStyle(
      { display: 'flex', gap: '0.75rem', marginTop: '1.25rem' },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      {}
    ),
    calculateButton: getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
      },
      { padding: '0.875rem', fontSize: '1rem', minHeight: '44px' },
      { padding: '0.875rem', fontSize: '1rem' },
      {}
    ),
    clearButton: getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: '#f1f5f9',
        color: '#475569',
      },
      { padding: '0.875rem', fontSize: '1rem', minHeight: '44px' },
      { padding: '0.875rem', fontSize: '1rem' },
      {}
    ),
    resultBox: getResponsiveStyle(
      {
        marginTop: '1.25rem',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '0.75rem',
        borderLeft: '4px solid #22c55e',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      {}
    ),
    resultLabel: {
      display: 'block',
      fontSize: '0.9rem',
      color: '#166534',
      fontWeight: 500,
      marginBottom: '0.5rem',
    },
    resultValue: getResponsiveStyle(
      { display: 'block', color: '#15803d', fontWeight: 700 },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      {}
    ),
    formulaHint: getResponsiveStyle(
      {
        marginTop: '0.75rem',
        background: '#fef3c7',
        borderRadius: '0.5rem',
        color: '#92400e',
        borderLeft: '3px solid #f59e0b',
      },
      { padding: '0.625rem', fontSize: '0.8rem' },
      { padding: '0.75rem', fontSize: '0.85rem' },
      {}
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { paddingLeft: '1.5rem', color: colors.textSecondary },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
    },
    insightCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(255, 240, 245, 0.8) 0%, rgba(255, 250, 240, 0.8) 100%)',
        borderLeft: '4px solid #ff5a70',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqItem: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.5)',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s ease',
    },
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.h2}>{t("toolTitle")}</h2>
          <p style={styles.summaryText}>{t("toolSubtitle")}</p>
        </div>

        <div style={styles.shapesGrid}>
          <div style={styles.shapeCard}>
            <div style={styles.shapeHeader}>
              <div style={styles.shapeIcon}>⚽</div>
              <h3 style={styles.shapeTitle}>{t("sphere")}</h3>
            </div>
            <form onSubmit={calculateSphere}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="sphere-radius">{t("radiusLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="sphere-radius"
                    step="any"
                    placeholder={t("radiusPlaceholder")}
                    value={sphereInputs.radius}
                    onChange={(e) => setSphereInputs({...sphereInputs, radius: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={sphereInputs.unit}
                    onChange={(e) => setSphereInputs({...sphereInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateButton}>{t("calculateButton")}</button>
                <button
                  type="button"
                  style={styles.clearButton}
                  onClick={() => {
                    setSphereInputs({ radius: '', unit: 'm' });
                    setSphereResult(null);
                  }}
                >
                  {t("clearButton")}
                </button>
              </div>
              {sphereResult !== null && (
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>{t("surfaceAreaLabel")}</span>
                  <span style={styles.resultValue}>
                    {formatNumber(sphereResult)} {unitLabels[sphereInputs.unit]}
                  </span>
                  <div style={styles.formulaHint}>{t("sphereFormula")}</div>
                </div>
              )}
            </form>
          </div>

          <div style={styles.shapeCard}>
            <div style={styles.shapeHeader}>
              <div style={styles.shapeIcon}>🔺</div>
              <h3 style={styles.shapeTitle}>{t("cone")}</h3>
            </div>
            <form onSubmit={calculateCone}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="cone-radius">{t("baseRadiusLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="cone-radius"
                    step="any"
                    placeholder={t("radiusPlaceholder")}
                    value={coneInputs.radius}
                    onChange={(e) => setConeInputs({...coneInputs, radius: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={coneInputs.unit}
                    onChange={(e) => setConeInputs({...coneInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="cone-height">{t("heightLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="cone-height"
                    step="any"
                    placeholder={t("heightPlaceholder")}
                    value={coneInputs.height}
                    onChange={(e) => setConeInputs({...coneInputs, height: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={coneInputs.unit}
                    onChange={(e) => setConeInputs({...coneInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateButton}>{t("calculateButton")}</button>
                <button
                  type="button"
                  style={styles.clearButton}
                  onClick={() => {
                    setConeInputs({ radius: '', height: '', unit: 'm' });
                    setConeResult(null);
                  }}
                >
                  {t("clearButton")}
                </button>
              </div>
              {coneResult !== null && (
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>{t("surfaceAreaLabel")}</span>
                  <span style={styles.resultValue}>
                    {formatNumber(coneResult)} {unitLabels[coneInputs.unit]}
                  </span>
                  <div style={styles.formulaHint}>{t("coneFormula")}</div>
                </div>
              )}
            </form>
          </div>

          <div style={styles.shapeCard}>
            <div style={styles.shapeHeader}>
              <div style={styles.shapeIcon}>🎲</div>
              <h3 style={styles.shapeTitle}>{t("cube")}</h3>
            </div>
            <form onSubmit={calculateCube}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="cube-edge">{t("edgeLengthLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="cube-edge"
                    step="any"
                    placeholder={t("edgePlaceholder")}
                    value={cubeInputs.edge}
                    onChange={(e) => setCubeInputs({...cubeInputs, edge: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={cubeInputs.unit}
                    onChange={(e) => setCubeInputs({...cubeInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateButton}>{t("calculateButton")}</button>
                <button
                  type="button"
                  style={styles.clearButton}
                  onClick={() => {
                    setCubeInputs({ edge: '', unit: 'm' });
                    setCubeResult(null);
                  }}
                >
                  {t("clearButton")}
                </button>
              </div>
              {cubeResult !== null && (
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>{t("surfaceAreaLabel")}</span>
                  <span style={styles.resultValue}>
                    {formatNumber(cubeResult)} {unitLabels[cubeInputs.unit]}
                  </span>
                  <div style={styles.formulaHint}>{t("cubeFormula")}</div>
                </div>
              )}
            </form>
          </div>

          <div style={styles.shapeCard}>
            <div style={styles.shapeHeader}>
              <div style={styles.shapeIcon}>🥫</div>
              <h3 style={styles.shapeTitle}>{t("cylinder")}</h3>
            </div>
            <form onSubmit={calculateCylinder}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="cylinder-radius">{t("baseRadiusLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="cylinder-radius"
                    step="any"
                    placeholder={t("radiusPlaceholder")}
                    value={cylinderInputs.radius}
                    onChange={(e) => setCylinderInputs({...cylinderInputs, radius: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={cylinderInputs.unit}
                    onChange={(e) => setCylinderInputs({...cylinderInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="cylinder-height">{t("heightLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="cylinder-height"
                    step="any"
                    placeholder={t("heightPlaceholder")}
                    value={cylinderInputs.height}
                    onChange={(e) => setCylinderInputs({...cylinderInputs, height: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={cylinderInputs.unit}
                    onChange={(e) => setCylinderInputs({...cylinderInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateButton}>{t("calculateButton")}</button>
                <button
                  type="button"
                  style={styles.clearButton}
                  onClick={() => {
                    setCylinderInputs({ radius: '', height: '', unit: 'm' });
                    setCylinderResult(null);
                  }}
                >
                  {t("clearButton")}
                </button>
              </div>
              {cylinderResult !== null && (
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>{t("surfaceAreaLabel")}</span>
                  <span style={styles.resultValue}>
                    {formatNumber(cylinderResult)} {unitLabels[cylinderInputs.unit]}
                  </span>
                  <div style={styles.formulaHint}>{t("cylinderFormula")}</div>
                </div>
              )}
            </form>
          </div>

          <div style={styles.shapeCard}>
            <div style={styles.shapeHeader}>
              <div style={styles.shapeIcon}>📦</div>
              <h3 style={styles.shapeTitle}>{t("rectangularPrism")}</h3>
            </div>
            <form onSubmit={calculateRectangular}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="rect-length">{t("lengthLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="rect-length"
                    step="any"
                    placeholder={t("lengthPlaceholder")}
                    value={rectangularInputs.length}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, length: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={rectangularInputs.unit}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="rect-width">{t("widthLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="rect-width"
                    step="any"
                    placeholder={t("widthPlaceholder")}
                    value={rectangularInputs.width}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, width: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={rectangularInputs.unit}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="rect-height">{t("heightLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="rect-height"
                    step="any"
                    placeholder={t("heightPlaceholder")}
                    value={rectangularInputs.height}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, height: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={rectangularInputs.unit}
                    onChange={(e) => setRectangularInputs({...rectangularInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateButton}>{t("calculateButton")}</button>
                <button
                  type="button"
                  style={styles.clearButton}
                  onClick={() => {
                    setRectangularInputs({ length: '', width: '', height: '', unit: 'm' });
                    setRectangularResult(null);
                  }}
                >
                  {t("clearButton")}
                </button>
              </div>
              {rectangularResult !== null && (
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>{t("surfaceAreaLabel")}</span>
                  <span style={styles.resultValue}>
                    {formatNumber(rectangularResult)} {unitLabels[rectangularInputs.unit]}
                  </span>
                  <div style={styles.formulaHint}>{t("rectangularFormula")}</div>
                </div>
              )}
            </form>
          </div>

          <div style={styles.shapeCard}>
            <div style={styles.shapeHeader}>
              <div style={styles.shapeIcon}>💊</div>
              <h3 style={styles.shapeTitle}>{t("capsule")}</h3>
            </div>
            <form onSubmit={calculateCapsule}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="capsule-radius">{t("baseRadiusLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="capsule-radius"
                    step="any"
                    placeholder={t("radiusPlaceholder")}
                    value={capsuleInputs.radius}
                    onChange={(e) => setCapsuleInputs({...capsuleInputs, radius: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={capsuleInputs.unit}
                    onChange={(e) => setCapsuleInputs({...capsuleInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="capsule-height">{t("heightLabel")}</label>
                <div style={styles.inputWrapper}>
                  <input
                    type="number"
                    id="capsule-height"
                    step="any"
                    placeholder={t("heightPlaceholder")}
                    value={capsuleInputs.height}
                    onChange={(e) => setCapsuleInputs({...capsuleInputs, height: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <select
                    value={capsuleInputs.unit}
                    onChange={(e) => setCapsuleInputs({...capsuleInputs, unit: e.target.value})}
                    style={styles.select}
                  >
                    <option value="m">{t("meters")}</option>
                    <option value="cm">{t("centimeters")}</option>
                    <option value="mm">{t("millimeters")}</option>
                    <option value="ft">{t("feet")}</option>
                    <option value="in">{t("inches")}</option>
                  </select>
                </div>
              </div>
              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.calculateButton}>{t("calculateButton")}</button>
                <button
                  type="button"
                  style={styles.clearButton}
                  onClick={() => {
                    setCapsuleInputs({ radius: '', height: '', unit: 'm' });
                    setCapsuleResult(null);
                  }}
                >
                  {t("clearButton")}
                </button>
              </div>
              {capsuleResult !== null && (
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>{t("surfaceAreaLabel")}</span>
                  <span style={styles.resultValue}>
                    {formatNumber(capsuleResult)} {unitLabels[capsuleInputs.unit]}
                  </span>
                  <div style={styles.formulaHint}>{t("capsuleFormula")}</div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("howToStep1")}</li>
          <li style={styles.listItem}>{t("howToStep2")}</li>
          <li style={styles.listItem}>{t("howToStep3")}</li>
          <li style={styles.listItem}>{t("howToStep4")}</li>
          <li style={styles.listItem}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight1")}</p>
        </div>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight2")}</p>
        </div>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight3")}</p>
        </div>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight4")}</p>
        </div>
        <div style={styles.insightCard}>
          <p style={styles.paragraph}>{t("insight5")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '1.5rem'}}>{t("unitsTitle")}</h3>
        <p style={styles.paragraph}>{t("unitsDescription")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '1.5rem'}}>{t("formulasTitle")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t("sphere")}:</strong> {t("sphereFormulaDetail")}</li>
          <li style={styles.listItem}><strong>{t("cone")}:</strong> {t("coneFormulaDetail")}</li>
          <li style={styles.listItem}><strong>{t("cube")}:</strong> {t("cubeFormulaDetail")}</li>
          <li style={styles.listItem}><strong>{t("cylinder")}:</strong> {t("cylinderFormulaDetail")}</li>
          <li style={styles.listItem}><strong>{t("rectangularPrism")}:</strong> {t("rectangularFormulaDetail")}</li>
          <li style={styles.listItem}><strong>{t("capsule")}:</strong> {t("capsuleFormulaDetail")}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '1.5rem'}}>{t("applicationsTitle")}</h3>
        <p style={styles.paragraph}>{t("applicationsDescription")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq5Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq6Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.graphcalc.com/surface-area-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference1")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.ebsco.com/research-starters/science/understanding-surface-area"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference2")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://testbook.com/maths/surface-area"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference3")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.cuemath.com/measurement/surface-area/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference4")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://en.wikipedia.org/wiki/Surface_area"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference5")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.geeksforgeeks.org/maths/real-life-applications-of-surface-area/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t("reference6")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}